import apiClient from './apiClient';
import type { ApiResponse, ConsultationPrescription } from '../types/api';

export interface CreateConsultationPrescriptionRequest {
    consultation_id: number;
    medication_name: string;
    dosage: string;
    frequency: string;
    duration: string;
    instructions?: string;
}

export interface UpdateConsultationPrescriptionRequest {
    medication_name?: string;
    dosage?: string;
    frequency?: string;
    duration?: string;
    instructions?: string;
}

export const consultationPrescriptionService = {
    async getByConsultation(consultationId: number): Promise<ConsultationPrescription[]> {
        const response = await apiClient.get<ApiResponse<ConsultationPrescription[]>>(
            `/consultation-prescriptions?consultation_id=${consultationId}`
        );
        return response.data.data;
    },

    async create(data: CreateConsultationPrescriptionRequest): Promise<ConsultationPrescription> {
        const response = await apiClient.post<ApiResponse<ConsultationPrescription>>(
            '/consultation-prescriptions',
            data
        );
        return response.data.data;
    },

    async update(id: number, data: UpdateConsultationPrescriptionRequest): Promise<ConsultationPrescription> {
        const response = await apiClient.put<ApiResponse<ConsultationPrescription>>(
            `/consultation-prescriptions/${id}`,
            data
        );
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/consultation-prescriptions/${id}`);
    },

    /**
     * Void a signed prescription and issue its signed replacement in one
     * atomic call (clinical-record-amendments R5). `reason` is the void reason.
     */
    async reissue(id: number, data: Omit<CreateConsultationPrescriptionRequest, 'consultation_id'> & { reason: string }): Promise<ConsultationPrescription> {
        const response = await apiClient.post<ApiResponse<ConsultationPrescription>>(
            `/consultation-prescriptions/${id}/reissue`,
            data
        );
        return response.data.data;
    },

    async downloadPdf(consultationId: number): Promise<void> {
        const response = await apiClient.get<Blob>(
            `/consultation-prescriptions/${consultationId}/pdf`,
            { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `receta_${consultationId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },
};

export default consultationPrescriptionService;
